import { decorateChunk } from './caveProps';
import { CaveSdf, type CaveShape, type V3 } from './sdf';
import { type ChunkMesh, type ChunkSpec, type Clip, chunkGrid, meshChunk } from './surfaceNets';

/**
 * Meshing a cave, as a sequence of chunks: shared by the worker and the main-thread
 * fallback. No three.js.
 */
export interface MeshJob {
  key: string;
  shape: CaveShape;
  clips: Clip[];
  /** Mesh outward from here (cave-local): the gate the player is coming in by. */
  from: V3;
  cell: number;
  n: number;
}

/** The chunks a job needs (those the walls can pass through), nearest `from` first. */
export function planChunks(job: MeshJob, sdf: CaveSdf): ChunkSpec[] {
  const all = chunkGrid(sdf.bounds(2), job.cell, job.n);
  const size = job.cell * job.n;
  const keep: { spec: ChunkSpec; d: number }[] = [];
  for (const spec of all) {
    const box = {
      min: spec.origin,
      max: [
        spec.origin[0] + size + job.cell,
        spec.origin[1] + size + job.cell,
        spec.origin[2] + size + job.cell,
      ] as V3,
    };
    if (sdf.candidates(box).length === 0) continue;
    const c = spec.origin.map((v) => v + size / 2);
    keep.push({ spec, d: Math.hypot(c[0] - job.from[0], c[1] - job.from[1], c[2] - job.from[2]) });
  }
  keep.sort((a, b) => a.d - b.d);
  return keep.map((k) => k.spec);
}

/** Meshes (and decorates) the job one chunk per call of the returned function; null when done. */
export function chunkStepper(job: MeshJob): () => { mesh: ChunkMesh | null } | null {
  const sdf = new CaveSdf(job.shape);
  const specs = planChunks(job, sdf);
  let i = 0;
  return () => {
    if (i >= specs.length) return null;
    const spec = specs[i++];
    const mesh = meshChunk(
      sdf,
      spec,
      sdf.candidates({ min: spec.origin, max: spec.origin.map((v) => v + (job.n + 1) * job.cell) as V3 }),
      job.clips,
    );
    return { mesh: mesh && decorateChunk(mesh, sdf, spec, job.shape.seed) };
  };
}

/** The buffers of a chunk, to transfer to the main thread rather than copy. */
export function chunkBuffers(m: ChunkMesh): ArrayBuffer[] {
  const arrays = [m.positions, m.normals, m.looks, m.indices, m.glow, m.hullPositions, m.hullIndices];
  return arrays.filter((a): a is NonNullable<typeof a> => !!a).map((a) => a.buffer as ArrayBuffer);
}

/** Worker messages. */
export type ToWorker = { type: 'mesh'; job: MeshJob } | { type: 'cancel'; key: string };
export type FromWorker =
  | { type: 'chunk'; key: string; mesh: ChunkMesh }
  | { type: 'done'; key: string; chunks: number; ms: number };
